import { useState, useEffect, Fragment } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';

// Get the AIMe server URL from environment variables
const AIME_SERVER_URL = process.env.REACT_APP_AIME_SERVER_URL || 'http://localhost:4000';

// Reuse the existing ALPS token
const getToken = () => sessionStorage.getItem('idToken');

const ConversationList = ({ onSelectThread, onNewChat, selectedThreadId, isTenantUser = true }) => {
	const theme = useTheme();
	const [threads, setThreads] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [showLoadMore, setShowLoadMore] = useState(false);

	// Function to fetch threads
	const fetchThreads = async (offset = 0) => {
		try {
			setLoading(true);
			setError(null);

			const response = await fetch(
				`${AIME_SERVER_URL}/threads?client_type=web${offset ? `&offset=${offset}` : ''
				}`,
				{
					method: 'GET',
					headers: {
						Authorization: `Bearer ${getToken()}`,
						'Content-Type': 'application/json',
					},
				}
			);

			if (!response.ok) {
				throw new Error(`Error fetching threads: ${response.status}`);
			}

			const data = await response.json();
			const fetchedThreads = data.threads || [];

			if (offset === 0) {
				setThreads(fetchedThreads);
			} else {
				setThreads((prev) => [...prev, ...fetchedThreads]);
			}

			// If we got exactly 10 threads (likely the page size), show load more button
			setShowLoadMore(fetchedThreads.length === 10);
		} catch (error) {
			console.error('Failed to fetch threads:', error);
			setError('Failed to load conversation history.');
		} finally {
			setLoading(false);
		}
	};

	// Load threads on component mount
	useEffect(() => {
		if (isTenantUser) {
			fetchThreads();
		}
	}, [isTenantUser]);

	// Handle loading more threads
	const handleLoadMore = () => {
		fetchThreads(threads.length);
	};

	// Format date for display
	const formatDate = (dateString) => {
		if (!dateString) return '';

		const date = new Date(dateString);
		const now = new Date();
		const diffMs = now - date;
		const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

		// If less than 24 hours ago, show time
		if (diffDays < 1) {
			return date.toLocaleTimeString([], {
				hour: '2-digit',
				minute: '2-digit',
			});
		}
		// If less than 7 days ago, show day of week
		else if (diffDays < 7) {
			return (
				date.toLocaleDateString([], { weekday: 'short' }) +
				' ' +
				date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
			);
		}
		// Otherwise show date
		else {
			return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
		}
	};

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
				<Typography variant='h6' component='h2' sx={{ mb: 1 }}>
					Conversations
				</Typography>
				{isTenantUser && (
					<Button
						variant='contained'
						color='primary'
						startIcon={<AddIcon />}
						fullWidth
						onClick={onNewChat}
						size='small'
					>
						New Chat
					</Button>
				)}
			</Box>

			{loading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
					<CircularProgress size={24} />
				</Box>
			) : error ? (
				<Box sx={{ p: 2 }}>
					<Typography color='error'>{error}</Typography>
				</Box>
			) : threads.length === 0 ? (
				<Box sx={{ p: 2 }}>
					<Typography variant='body2' color='text.secondary'>
						No conversations yet. Start a new chat to get started.
					</Typography>
					<Button variant='outlined' onClick={onNewChat}>
						Start a new chat
					</Button>
				</Box>
			) : (
				<List
					sx={{
						overflow: 'auto',
						flex: 1,
						'& .MuiListItemButton-root.selected': {
							backgroundColor:
								theme.palette.mode === 'dark'
									? 'rgba(255, 255, 255, 0.08)'
									: 'rgba(0, 0, 0, 0.04)',
							borderLeft: `3px solid ${theme.palette.primary.main}`,
						},
					}}
				>
					{threads.map((thread) => (
						<Fragment key={thread.thread_id || thread.id}>
							<ListItemButton
								onClick={() => {
									const threadId = thread.thread_id || thread.id;
									onSelectThread(threadId);
								}}
								sx={{
									px: 2,
									py: 1.5,
									borderLeft: '3px solid transparent',
								}}
								className={
									thread.thread_id === selectedThreadId ||
										thread.id === selectedThreadId
										? 'selected'
										: ''
								}
							>
								<ListItemText
									primary={
										<Typography
											variant='body2'
											sx={{
												fontWeight:
													thread.thread_id === selectedThreadId ||
														thread.id === selectedThreadId
														? 600
														: 400,
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
												mb: 0.5,
											}}
										>
											{thread.values?.summary || 'New conversation'}
										</Typography>
									}
									secondary={
										<Typography
											variant='caption'
											sx={{ color: theme.palette.text.secondary }}
										>
											{formatDate(thread.created_at)}
										</Typography>
									}
								/>
							</ListItemButton>
							<Divider variant='inset' component='li' />
						</Fragment>
					))}

					{showLoadMore && (
						<ListItem sx={{ justifyContent: 'center', p: 2 }}>
							<Button
								variant='text'
								onClick={handleLoadMore}
								disabled={loading}
								sx={{ textTransform: 'none' }}
							>
								{loading ? <CircularProgress size={24} /> : 'Load more'}
							</Button>
						</ListItem>
					)}
				</List>
			)}
		</Box>
	);
};

export default ConversationList;
